uniform vec2 resolution;
uniform float time;
varying vec2 vUv;

// x: rotation, counter clock wise
// y: distance from center (1==half height)
vec2 toPolarUV(vec2 uv, vec2 resolution) {
    vec2 p = uv;
    p -= 0.5;
    p.x = p.x * (resolution.x / resolution.y);

    float pixelAngle = atan(p.x, p.y);
    float pixelDistance = length(p) * 2.0;

    return vec2(pixelAngle / 6.28318530717958623199592693708 + 0.5, pixelDistance);
}

// Returns a cell in vec3 formation:
//  xy: -1.0:1.0 position in cell
//  zw: x and y index of cell
vec4 makeCell(vec2 st, float width) {
    st = st + vec2(width / 2.); // This centers the grid
    vec2 p = (mod(st, width) / width) * 2.0 - 1.0;
    return vec4(p, floor(st / width));
}

vec4 makeRings(vec2 polar, vec2 width) {
    vec2 p = mod(polar, width) / width;
    return vec4(p, floor(polar / width));
}

float easeOutCirc(float x) {
    return sqrt(1.0 - pow(x - 1.0, 2.0));
}

vec3 interpolate6ColorsDiscrete(float v, vec3 color1, vec3 color2, vec3 color3, vec3 color4, vec3 color5, vec3 color6, float smoothFactor) {
    vec3 result;

    // Normalize v to be between 0 and 1
    v = clamp(v, 0.0, 1.0);

    // Clamp smoothFactor to be between 0 and 1
    smoothFactor = clamp(smoothFactor, 0.0, 1.0);

    // Determine the current segment and use smoothstep for a smooth transition between segments
    float segmentLength = 1.0 / 5.0;

    if(v < 0.2) {
        float t = smoothstep(0.2 - smoothFactor * segmentLength, 0.2, v);
        result = mix(color1, color2, t);
    } else if(v < 0.4) {
        float t = smoothstep(0.4 - smoothFactor * segmentLength, 0.4, v);
        result = mix(color2, color3, t);
    } else if(v < 0.6) {
        float t = smoothstep(0.6 - smoothFactor * segmentLength, 0.6, v);
        result = mix(color3, color4, t);
    } else if(v < 0.8) {
        float t = smoothstep(0.8 - smoothFactor * segmentLength, 0.8, v);
        result = mix(color4, color5, t);
    } else {
        float t = smoothstep(1.0 - smoothFactor * segmentLength, 1.0, v);
        result = mix(color5, color6, t);
    }

    return result;
}

// https://www.shadertoy.com/view/ttcyRS
vec3 oklab_mix(vec3 colA, vec3 colB, float h) {
    // https://bottosson.github.io/posts/oklab
    const mat3 kCONEtoLMS = mat3(0.4121656120, 0.2118591070, 0.0883097947, 0.5362752080, 0.6807189584, 0.2818474174, 0.0514575653, 0.1074065790, 0.6302613616);
    const mat3 kLMStoCONE = mat3(4.0767245293, -1.2681437731, -0.0041119885, -3.3072168827, 2.6093323231, -0.7034763098, 0.2307590544, -0.3411344290, 1.7068625689);

    // rgb to cone (arg of pow can't be negative)
    vec3 lmsA = pow(kCONEtoLMS * colA, vec3(1.0 / 3.0));
    vec3 lmsB = pow(kCONEtoLMS * colB, vec3(1.0 / 3.0));
    // lerp
    vec3 lms = mix(lmsA, lmsB, h);
    // gain in the middle (no oaklab anymore, but looks better?)
 // lms *= 1.0+0.2*h*(1.0-h);
    // cone to rgb
    return kLMStoCONE * (lms * lms * lms);
}

const float PI  = 3.14159265358;
const float TAU = 6.28318530717;

// lch = (lightness, chromaticity, hue)
vec3 oklch2oklab(vec3 lch) {
  return vec3(lch.x, lch.y * cos(lch.z * TAU), lch.y * sin(lch.z * TAU));
}

// https://www.shadertoy.com/view/cdtBRM

// oklab = (lightness, red_greenness, blue_yelowness)
vec3 oklab2lrgb(vec3 oklab) {
    vec3 lms = oklab * mat3(1,  0.3963377774,  0.2158037573,
                            1, -0.1055613458, -0.0638541728,
                            1, -0.0894841775, -1.2914855480);
    lms *= lms * lms;
    return lms * mat3( 4.0767416621, -3.3077115913,  0.2309699292, 
                      -1.2684380046,  2.6097574011, -0.3413193965, 
                      -0.0041960863, -0.7034186147,  1.7076147010);
}

vec3 lrgb2oklab(vec3 lrgb) {
    vec3 lms = lrgb * mat3(0.4121656120, 0.5362752080, 0.0514575653,
                           0.2118591070, 0.6807189584, 0.1074065790,
                           0.0883097947, 0.2818474174, 0.6302613616);
    return pow(lms, vec3(1.0 / 3.0)) * mat3(0.2104542553,  0.7936177850, -0.0040720468,
                                            1.9779984951, -2.4285922050,  0.4505937099,
                                            0.0259040371,  0.7827717662, -0.8086757660);
}

vec3 lrgb2rgb(vec3 x) {
    vec3 xlo = 12.92 * x;
    vec3 xhi = 1.055 * pow(x, vec3(1.0 / 2.4)) - 0.055;
    return mix(xlo, xhi, step(vec3(0.0031308), x));
}

vec3 rgb2lrgb(vec3 x) {
    vec3 xlo = x / 12.92;
    vec3 xhi = pow((x + 0.055) / 1.055, vec3(2.4));
    return mix(xlo, xhi, step(0.04045, x));
}

void main() {

    vec2 uv = vUv;

    vec2 polar = toPolarUV(uv, resolution);
    float size = 5.0;

    float ringCount = sin(time * 0.0001) * 15.0;
    float ringSplitCount = 5.0;

    float speedMultiplyer = 0.0001;
    polar.x += time * speedMultiplyer;
    vec4 ringForwards = makeRings(polar, vec2(1.0 / ringSplitCount, 1.0 / ringCount));
    polar.x -= time * (speedMultiplyer * 2.0);
    vec4 ringBackwards = makeRings(polar, vec2(1.0 / ringSplitCount, 1.0 / ringCount));

    vec4 ring = mix(ringForwards, ringBackwards, step(0.5, mod(ringForwards.w, 2.0)));

    float v = 0.0;

    float shadow = 1.0 - (ring.y * ring.y * ring.y * ring.y);
    shadow = max(shadow, (ring.y * ring.y));

    vec2 ringIndex = ring.zw;
    v = mod(ringIndex.x + ringIndex.y, ringSplitCount) / (ringSplitCount - 1.0);
    v += ring.x / 2.0;

    vec3 ok = oklch2oklab(vec3(0.8 + (ring.y * 0.1) + (cos(time * 0.0025) * 0.05), 0.25 + (sin(time * 0.001) * 0.05), ((ringIndex.y * (time * 0.001)) * 0.05) + v * 0.2));
    vec3 color = oklab2lrgb(ok);
    color = lrgb2rgb(color);
    color = clamp(color, vec3(0), vec3(1));

    // v = (v * 0.8 + 0.2;
    color = color * shadow;

    gl_FragColor = vec4(color, 1.0);

}